import { type Static, Type } from "@sinclair/typebox";
import { Value } from "@sinclair/typebox/value";
import {
  WorkforceAccountSchema,
  WorkforceCoachExtensionSchema,
  WorkforceEmploymentSchema,
  WorkforceOwnershipSchema,
  WorkforcePersonSchema,
  WorkforceRoleSchema,
} from "./workforce.js";
import {
  CancelWorkforceInvitationCommandSchema,
  CreateWorkforceInvitationCommandSchema,
  CreateWorkforcePasswordResetCommandSchema,
  ReissueWorkforceInvitationCommandSchema,
} from "./workforce-invitation.js";

const WorkforceManagementIdSchema = Type.String({ minLength: 1, maxLength: 512 });
const WorkforceManagementDateTimeSchema = Type.String({ format: "date-time" });
const CanonicalWorkforceEmployeeRoleSchema = Type.Union([
  Type.Literal("MANAGER"),
  Type.Literal("CASHIER"),
  Type.Literal("WAITER"),
]);
export type CanonicalWorkforceEmployeeRole = Static<typeof CanonicalWorkforceEmployeeRoleSchema>;

export const WorkforceMigrationConflictTypeSchema = Type.Union([
  Type.Literal("UNVERIFIED_PHONE_COLLISION"),
  Type.Literal("AMBIGUOUS_ACCOUNT_BINDING"),
  Type.Literal("EMPLOYMENT_STATUS_UNCONFIRMED"),
  Type.Literal("OWNERSHIP_STATUS_UNCONFIRMED"),
]);
export type WorkforceMigrationConflictType = Static<typeof WorkforceMigrationConflictTypeSchema>;

export const WorkforceMigrationConflictStatusSchema = Type.Union([
  Type.Literal("OPEN"),
  Type.Literal("RESOLVED"),
]);
export type WorkforceMigrationConflictStatus = Static<
  typeof WorkforceMigrationConflictStatusSchema
>;

const UpsertCanonicalWorkforcePersonSchema = Type.Object(
  {
    personId: WorkforceManagementIdSchema,
    displayName: Type.String({ minLength: 1, maxLength: 80 }),
    primaryPhone: Type.Union([
      Type.String({
        minLength: 6,
        maxLength: 40,
        pattern: "^[0-9+\\s()（）._:/／-]+$",
      }),
      Type.Null(),
    ]),
  },
  { additionalProperties: false },
);

const UpsertCanonicalWorkforceActiveEmploymentSchema = Type.Object(
  {
    storeId: WorkforceManagementIdSchema,
    employmentNumber: Type.String({ minLength: 1, maxLength: 80 }),
    positionTitle: Type.String({ minLength: 1, maxLength: 80 }),
    status: Type.Literal("ACTIVE"),
    roles: Type.Array(CanonicalWorkforceEmployeeRoleSchema, {
      minItems: 1,
      maxItems: 4,
      uniqueItems: true,
    }),
  },
  { additionalProperties: false },
);

const UpsertCanonicalWorkforceEndedEmploymentSchema = Type.Object(
  {
    storeId: WorkforceManagementIdSchema,
    employmentNumber: Type.String({ minLength: 1, maxLength: 80 }),
    positionTitle: Type.String({ minLength: 1, maxLength: 80 }),
    status: Type.Literal("ENDED"),
    roles: Type.Array(CanonicalWorkforceEmployeeRoleSchema, { maxItems: 0 }),
  },
  { additionalProperties: false },
);

const UpsertCanonicalWorkforceEmploymentCommandFields = {
  person: UpsertCanonicalWorkforcePersonSchema,
  reason: Type.String({ minLength: 2, maxLength: 500 }),
  idempotencyKey: Type.String({ minLength: 8, maxLength: 128 }),
};

/**
 * Internal canonical person and one store-scoped employment mutation.
 * Authentication, authorization, tenant scope, account activation and HTTP
 * transport deliberately live outside this domain contract.
 */
const UpsertCanonicalWorkforceNewEmploymentCommandSchema = Type.Object(
  {
    ...UpsertCanonicalWorkforceEmploymentCommandFields,
    personMode: Type.Literal("NEW_PERSON"),
    employment: UpsertCanonicalWorkforceActiveEmploymentSchema,
    expectedAggregateVersion: Type.Null(),
  },
  { additionalProperties: false },
);

const UpsertCanonicalWorkforceExistingEmploymentCommandSchema = Type.Object(
  {
    ...UpsertCanonicalWorkforceEmploymentCommandFields,
    personMode: Type.Literal("EXISTING_PERSON"),
    employment: Type.Union([
      UpsertCanonicalWorkforceActiveEmploymentSchema,
      UpsertCanonicalWorkforceEndedEmploymentSchema,
    ]),
    expectedAggregateVersion: Type.Integer({ minimum: 1 }),
  },
  { additionalProperties: false },
);

const UpsertCanonicalWorkforceExistingEndedEmploymentCommandSchema = Type.Object(
  {
    ...UpsertCanonicalWorkforceEmploymentCommandFields,
    personMode: Type.Literal("EXISTING_PERSON"),
    employment: UpsertCanonicalWorkforceEndedEmploymentSchema,
    expectedAggregateVersion: Type.Integer({ minimum: 1 }),
  },
  { additionalProperties: false },
);

const UpsertCanonicalWorkforceExistingActiveEmploymentCommandSchema = Type.Object(
  {
    ...UpsertCanonicalWorkforceEmploymentCommandFields,
    personMode: Type.Literal("EXISTING_PERSON"),
    employment: UpsertCanonicalWorkforceActiveEmploymentSchema,
    expectedAggregateVersion: Type.Integer({ minimum: 1 }),
  },
  { additionalProperties: false },
);

export const UpsertCanonicalWorkforceEmploymentCommandSchema = Type.Union([
  UpsertCanonicalWorkforceNewEmploymentCommandSchema,
  UpsertCanonicalWorkforceExistingEmploymentCommandSchema,
]);
export type UpsertCanonicalWorkforceEmploymentCommand = Static<
  typeof UpsertCanonicalWorkforceEmploymentCommandSchema
>;

const UpsertCanonicalWorkforceEmploymentResultFields = {
  personId: WorkforceManagementIdSchema,
  displayName: Type.String({ minLength: 1, maxLength: 80 }),
  personVersion: Type.Integer({ minimum: 1 }),
  employmentId: WorkforceManagementIdSchema,
  storeId: WorkforceManagementIdSchema,
  employmentNumber: Type.String({ minLength: 1, maxLength: 80 }),
  positionTitle: Type.String({ minLength: 1, maxLength: 80 }),
  employmentVersion: Type.Integer({ minimum: 1 }),
  aggregateVersion: Type.Integer({ minimum: 1 }),
  projectionRevision: Type.Integer({ minimum: 1 }),
  reviewConflictIds: Type.Array(Type.String({ pattern: "^review_[a-f0-9]{32}$" }), {
    uniqueItems: true,
  }),
  startedAt: WorkforceManagementDateTimeSchema,
  createdAt: WorkforceManagementDateTimeSchema,
  updatedAt: WorkforceManagementDateTimeSchema,
};

export const UpsertCanonicalWorkforceEmploymentResultSchema = Type.Union([
  Type.Object(
    {
      ...UpsertCanonicalWorkforceEmploymentResultFields,
      employmentStatus: Type.Literal("ACTIVE"),
      roles: Type.Array(CanonicalWorkforceEmployeeRoleSchema, {
        minItems: 1,
        maxItems: 4,
        uniqueItems: true,
      }),
      endedAt: Type.Null(),
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      ...UpsertCanonicalWorkforceEmploymentResultFields,
      employmentStatus: Type.Literal("ENDED"),
      roles: Type.Array(CanonicalWorkforceEmployeeRoleSchema, { maxItems: 0 }),
      endedAt: WorkforceManagementDateTimeSchema,
    },
    { additionalProperties: false },
  ),
]);
export type UpsertCanonicalWorkforceEmploymentResult = Static<
  typeof UpsertCanonicalWorkforceEmploymentResultSchema
>;

/**
 * Internal command for disabling one already-bound canonical login account.
 * Tenant, store, actor, target person and target account are trusted transport
 * scope/path facts and deliberately cannot be repeated in this body.
 */
export const DisableCanonicalWorkforceAccountCommandSchema = Type.Object(
  {
    expectedAggregateVersion: Type.Integer({ minimum: 1 }),
    expectedIdentityVersion: Type.Integer({ minimum: 1 }),
    reason: Type.String({ minLength: 2, maxLength: 500 }),
    idempotencyKey: Type.String({ minLength: 8, maxLength: 128 }),
  },
  { additionalProperties: false },
);
export type DisableCanonicalWorkforceAccountCommand = Static<
  typeof DisableCanonicalWorkforceAccountCommandSchema
>;

const WorkforceMutationPendingCommonFields = {
  personId: WorkforceManagementIdSchema,
  commandSha256: Type.String({ pattern: "^[a-f0-9]{64}$" }),
  recordedAt: WorkforceManagementDateTimeSchema,
};

/**
 * Exact command retained by the Gateway while a canonical workforce mutation
 * has an unknown outcome. Scope and actor remain authenticated transport facts
 * and are deliberately excluded from this browser response.
 */
export const WorkforceMutationPendingSchema = Type.Union([
  Type.Object(
    {
      kind: Type.Literal("ACCOUNT_INVITE"),
      ...WorkforceMutationPendingCommonFields,
      request: CreateWorkforceInvitationCommandSchema,
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      kind: Type.Literal("ACCOUNT_INVITATION_CANCEL"),
      ...WorkforceMutationPendingCommonFields,
      request: CancelWorkforceInvitationCommandSchema,
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      kind: Type.Literal("ACCOUNT_INVITATION_REISSUE"),
      ...WorkforceMutationPendingCommonFields,
      request: ReissueWorkforceInvitationCommandSchema,
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      kind: Type.Literal("ACCOUNT_PASSWORD_RESET"),
      ...WorkforceMutationPendingCommonFields,
      accountId: WorkforceManagementIdSchema,
      request: CreateWorkforcePasswordResetCommandSchema,
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      kind: Type.Literal("EMPLOYMENT_CREATE"),
      ...WorkforceMutationPendingCommonFields,
      request: UpsertCanonicalWorkforceNewEmploymentCommandSchema,
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      kind: Type.Literal("EMPLOYMENT_PROFILE_UPDATE"),
      ...WorkforceMutationPendingCommonFields,
      request: UpsertCanonicalWorkforceExistingEmploymentCommandSchema,
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      kind: Type.Literal("EMPLOYMENT_ROLE_CHANGE"),
      ...WorkforceMutationPendingCommonFields,
      request: UpsertCanonicalWorkforceExistingEmploymentCommandSchema,
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      kind: Type.Literal("EMPLOYMENT_END"),
      ...WorkforceMutationPendingCommonFields,
      request: UpsertCanonicalWorkforceExistingEndedEmploymentCommandSchema,
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      kind: Type.Literal("EMPLOYMENT_REHIRE"),
      ...WorkforceMutationPendingCommonFields,
      request: UpsertCanonicalWorkforceExistingActiveEmploymentCommandSchema,
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      kind: Type.Literal("ACCOUNT_DISABLE"),
      ...WorkforceMutationPendingCommonFields,
      accountId: WorkforceManagementIdSchema,
      personDisplayName: Type.String({ minLength: 1, maxLength: 80 }),
      accountLoginHint: Type.String({ minLength: 1, maxLength: 80 }),
      request: DisableCanonicalWorkforceAccountCommandSchema,
    },
    { additionalProperties: false },
  ),
]);
export type WorkforceMutationPending = Static<typeof WorkforceMutationPendingSchema>;

export const WorkforceOffboardingBlockerCodeSchema = Type.Union([
  Type.Literal("SELF_OFFBOARDING"),
  Type.Literal("MANAGER_SCOPE_FORBIDDEN"),
  Type.Literal("OWNER_IDENTITY_ACTIVE"),
  Type.Literal("COACH_IDENTITY_ACTIVE"),
  Type.Literal("SPECIAL_ROLE_ACTIVE"),
  Type.Literal("EMPLOYMENT_NOT_ACTIVE"),
  Type.Literal("MIGRATION_CONFLICT_OPEN"),
  Type.Literal("CASH_SHIFT_OPEN"),
  Type.Literal("SERVICE_REQUEST_ASSIGNED"),
  Type.Literal("BUSINESS_SESSION_OPEN"),
]);
export type WorkforceOffboardingBlockerCode = Static<typeof WorkforceOffboardingBlockerCodeSchema>;

export const WorkforceOffboardingBlockerSchema = Type.Object(
  {
    code: WorkforceOffboardingBlockerCodeSchema,
    count: Type.Integer({ minimum: 1 }),
  },
  { additionalProperties: false },
);
export type WorkforceOffboardingBlocker = Static<typeof WorkforceOffboardingBlockerSchema>;

/**
 * Current-store, read-only preview assembled from the accepted workforce
 * projection and live Gateway responsibilities immediately before offboarding.
 */
export const WorkforceOffboardingPreviewSchema = Type.Object(
  {
    personId: WorkforceManagementIdSchema,
    storeId: WorkforceManagementIdSchema,
    employmentId: Type.Union([WorkforceManagementIdSchema, Type.Null()]),
    projectionRevision: Type.Integer({ minimum: 1 }),
    canEnd: Type.Boolean(),
    activeAccounts: Type.Integer({ minimum: 0 }),
    activeSessionsToRevoke: Type.Integer({ minimum: 0 }),
    otherStoreActiveEmployment: Type.Boolean(),
    historyWillBePreserved: Type.Literal(true),
    blockers: Type.Array(WorkforceOffboardingBlockerSchema),
    previewedAt: WorkforceManagementDateTimeSchema,
  },
  { additionalProperties: false },
);
export type WorkforceOffboardingPreview = Static<typeof WorkforceOffboardingPreviewSchema>;

/** Minimal receipt: no login identity, contact value, credential or ownership facts. */
export const DisableCanonicalWorkforceAccountResultSchema = Type.Object(
  {
    personId: WorkforceManagementIdSchema,
    accountId: WorkforceManagementIdSchema,
    storeId: WorkforceManagementIdSchema,
    accountStatus: Type.Literal("DISABLED"),
    identityVersion: Type.Integer({ minimum: 1 }),
    aggregateVersion: Type.Integer({ minimum: 1 }),
    projectionRevision: Type.Integer({ minimum: 1 }),
    updatedAt: WorkforceManagementDateTimeSchema,
  },
  { additionalProperties: false },
);
export type DisableCanonicalWorkforceAccountResult = Static<
  typeof DisableCanonicalWorkforceAccountResultSchema
>;

const WorkforceManagementContactFields = {
  id: WorkforceManagementIdSchema,
  verified: Type.Boolean(),
  primary: Type.Boolean(),
  loginIdentity: Type.Boolean(),
  createdAt: WorkforceManagementDateTimeSchema,
  updatedAt: WorkforceManagementDateTimeSchema,
};

const WorkforceMaskedPhoneValueSchema = Type.String({
  maxLength: 160,
  pattern:
    "^(?:\\*{4}|[^*\\r\\n]\\*{4,}[^*\\r\\n]|[^*\\r\\n]{2}\\*{4,}[^*\\r\\n]{2}|[^*\\r\\n]{3}\\*{4,}[^*\\r\\n]{4})$",
});
const WorkforceMaskedWechatValueSchema = Type.String({
  maxLength: 160,
  pattern: "^(?:\\*{3}|[^*\\r\\n]\\*{3}[^*\\r\\n])$",
});

export const WorkforceManagementContactSchema = Type.Union([
  Type.Object(
    {
      ...WorkforceManagementContactFields,
      contactType: Type.Literal("PHONE"),
      displayValue: WorkforceMaskedPhoneValueSchema,
      visibility: Type.Literal("MASKED"),
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      ...WorkforceManagementContactFields,
      contactType: Type.Literal("WECHAT"),
      displayValue: WorkforceMaskedWechatValueSchema,
      visibility: Type.Literal("MASKED"),
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      ...WorkforceManagementContactFields,
      contactType: Type.Union([Type.Literal("PHONE"), Type.Literal("WECHAT")]),
      displayValue: Type.String({ minLength: 1, maxLength: 160 }),
      visibility: Type.Literal("FULL"),
    },
    { additionalProperties: false },
  ),
]);
export type WorkforceManagementContact = Static<typeof WorkforceManagementContactSchema>;

export const WorkforceDirectoryContactSummarySchema = Type.Union([
  Type.Object(
    {
      contactType: Type.Literal("PHONE"),
      displayValue: WorkforceMaskedPhoneValueSchema,
      visibility: Type.Literal("MASKED"),
      verified: Type.Boolean(),
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      contactType: Type.Literal("WECHAT"),
      displayValue: WorkforceMaskedWechatValueSchema,
      visibility: Type.Literal("MASKED"),
      verified: Type.Boolean(),
    },
    { additionalProperties: false },
  ),
]);
export type WorkforceDirectoryContactSummary = Static<
  typeof WorkforceDirectoryContactSummarySchema
>;

export const WorkforceDirectoryAccountSummarySchema = Type.Object(
  {
    accountId: WorkforceManagementIdSchema,
    status: Type.Union([
      Type.Literal("PENDING"),
      Type.Literal("ACTIVE"),
      Type.Literal("DISABLED"),
      Type.Literal("MIGRATION_REVIEW"),
    ]),
    identityVersion: Type.Integer({ minimum: 1 }),
  },
  { additionalProperties: false },
);

export const WorkforceDirectoryEmploymentSummarySchema = Type.Object(
  {
    employmentId: WorkforceManagementIdSchema,
    storeId: WorkforceManagementIdSchema,
    employmentNumber: Type.String({ minLength: 1, maxLength: 80 }),
    positionTitle: Type.String({ minLength: 1, maxLength: 80 }),
    status: Type.Union([
      Type.Literal("PENDING"),
      Type.Literal("ACTIVE"),
      Type.Literal("REVIEW_REQUIRED"),
      Type.Literal("ENDED"),
    ]),
    activeRoles: Type.Array(WorkforceRoleSchema, { uniqueItems: true }),
    startedAt: WorkforceManagementDateTimeSchema,
    endedAt: Type.Union([WorkforceManagementDateTimeSchema, Type.Null()]),
    version: Type.Integer({ minimum: 1 }),
  },
  { additionalProperties: false },
);

export const WorkforceDirectoryOwnershipSummarySchema = Type.Object(
  {
    ownershipId: WorkforceManagementIdSchema,
    status: Type.Union([
      Type.Literal("ACTIVE"),
      Type.Literal("REVIEW_REQUIRED"),
      Type.Literal("ENDED"),
    ]),
    version: Type.Integer({ minimum: 1 }),
  },
  { additionalProperties: false },
);

export const WorkforceDirectoryCoachSummarySchema = Type.Object(
  {
    storeId: WorkforceManagementIdSchema,
    coachId: WorkforceManagementIdSchema,
    employmentId: WorkforceManagementIdSchema,
    status: Type.Union([Type.Literal("ACTIVE"), Type.Literal("DISABLED")]),
    version: Type.Integer({ minimum: 1 }),
  },
  { additionalProperties: false },
);

export const WorkforceDirectoryEntrySchema = Type.Object(
  {
    personId: WorkforceManagementIdSchema,
    displayName: Type.String({ minLength: 1, maxLength: 80 }),
    personVersion: Type.Integer({ minimum: 1 }),
    aggregateVersion: Type.Integer({ minimum: 1 }),
    accounts: Type.Array(WorkforceDirectoryAccountSummarySchema),
    employments: Type.Array(WorkforceDirectoryEmploymentSummarySchema),
    ownerships: Type.Array(WorkforceDirectoryOwnershipSummarySchema),
    coachExtensions: Type.Array(WorkforceDirectoryCoachSummarySchema),
    primaryContact: Type.Union([WorkforceDirectoryContactSummarySchema, Type.Null()]),
    openConflictTypes: Type.Array(WorkforceMigrationConflictTypeSchema, { uniqueItems: true }),
    openConflictCount: Type.Integer({ minimum: 0 }),
    updatedAt: WorkforceManagementDateTimeSchema,
  },
  { additionalProperties: false },
);
export type WorkforceDirectoryEntry = Static<typeof WorkforceDirectoryEntrySchema>;

export const WorkforceDirectoryPageSchema = Type.Object(
  {
    tenantId: WorkforceManagementIdSchema,
    visibleStoreIds: Type.Array(WorkforceManagementIdSchema, {
      minItems: 1,
      uniqueItems: true,
    }),
    entries: Type.Array(WorkforceDirectoryEntrySchema),
    nextCursor: Type.Union([Type.String({ minLength: 1, maxLength: 1024 }), Type.Null()]),
  },
  { additionalProperties: false },
);
export type WorkforceDirectoryPage = Static<typeof WorkforceDirectoryPageSchema>;

export const WorkforceEmploymentRoleIntervalSchema = Type.Object(
  {
    role: WorkforceRoleSchema,
    grantedAt: WorkforceManagementDateTimeSchema,
    revokedAt: Type.Union([WorkforceManagementDateTimeSchema, Type.Null()]),
  },
  { additionalProperties: false },
);

export const WorkforceManagementEmploymentSchema = Type.Composite(
  [
    WorkforceEmploymentSchema,
    Type.Object({ roleIntervals: Type.Array(WorkforceEmploymentRoleIntervalSchema) }),
  ],
  { additionalProperties: false },
);
export type WorkforceManagementEmployment = Static<typeof WorkforceManagementEmploymentSchema>;

export const WorkforceMigrationConflictPersonSchema = Type.Object(
  {
    personId: Type.Union([WorkforceManagementIdSchema, Type.Null()]),
    displayName: Type.String({ minLength: 1, maxLength: 120 }),
    storeIds: Type.Array(WorkforceManagementIdSchema, { uniqueItems: true }),
    visibilityLimited: Type.Boolean(),
  },
  { additionalProperties: false },
);

export const WorkforceMigrationConflictSummarySchema = Type.Object(
  {
    id: Type.String({ pattern: "^review_[a-f0-9]{32}$" }),
    conflictType: WorkforceMigrationConflictTypeSchema,
    status: WorkforceMigrationConflictStatusSchema,
    title: Type.String({ minLength: 1, maxLength: 120 }),
    detail: Type.String({ minLength: 1, maxLength: 500 }),
    nextStep: Type.String({ minLength: 1, maxLength: 500 }),
    affectedPeople: Type.Array(WorkforceMigrationConflictPersonSchema, {
      minItems: 1,
      maxItems: 2,
    }),
    detectedAt: WorkforceManagementDateTimeSchema,
    resolvedAt: Type.Union([WorkforceManagementDateTimeSchema, Type.Null()]),
    resolutionSummary: Type.Union([Type.String({ minLength: 1, maxLength: 500 }), Type.Null()]),
  },
  { additionalProperties: false },
);
export type WorkforceMigrationConflictSummary = Static<
  typeof WorkforceMigrationConflictSummarySchema
>;

export const WorkforceMigrationConflictPageSchema = Type.Object(
  {
    tenantId: WorkforceManagementIdSchema,
    status: WorkforceMigrationConflictStatusSchema,
    items: Type.Array(WorkforceMigrationConflictSummarySchema),
    nextCursor: Type.Union([Type.String({ minLength: 1, maxLength: 1024 }), Type.Null()]),
  },
  { additionalProperties: false },
);
export type WorkforceMigrationConflictPage = Static<typeof WorkforceMigrationConflictPageSchema>;

export const WorkforcePersonDetailSchema = Type.Object(
  {
    tenantId: WorkforceManagementIdSchema,
    visibleStoreIds: Type.Array(WorkforceManagementIdSchema, {
      minItems: 1,
      uniqueItems: true,
    }),
    aggregateVersion: Type.Integer({ minimum: 1 }),
    person: WorkforcePersonSchema,
    accounts: Type.Array(WorkforceAccountSchema),
    contacts: Type.Array(WorkforceManagementContactSchema),
    employments: Type.Array(WorkforceManagementEmploymentSchema),
    ownerships: Type.Array(WorkforceOwnershipSchema),
    coachExtensions: Type.Array(WorkforceCoachExtensionSchema),
    conflicts: Type.Array(WorkforceMigrationConflictSummarySchema),
  },
  { additionalProperties: false },
);
export type WorkforcePersonDetail = Static<typeof WorkforcePersonDetailSchema>;

export function isWorkforceDirectoryPage(value: unknown): value is WorkforceDirectoryPage {
  return Value.Check(WorkforceDirectoryPageSchema, value);
}

export function isWorkforceMigrationConflictPage(
  value: unknown,
): value is WorkforceMigrationConflictPage {
  return Value.Check(WorkforceMigrationConflictPageSchema, value);
}

export function isWorkforcePersonDetail(value: unknown): value is WorkforcePersonDetail {
  if (!Value.Check(WorkforcePersonDetailSchema, value)) return false;
  if (value.person.tenantId !== value.tenantId) return false;
  const accountIds = new Set<string>();
  for (const account of value.accounts) {
    if (
      account.tenantId !== value.tenantId ||
      account.personId !== value.person.id ||
      accountIds.has(account.id)
    ) {
      return false;
    }
    accountIds.add(account.id);
  }
  return true;
}

export function isUpsertCanonicalWorkforceEmploymentCommand(
  value: unknown,
): value is UpsertCanonicalWorkforceEmploymentCommand {
  return Value.Check(UpsertCanonicalWorkforceEmploymentCommandSchema, value);
}

export function isUpsertCanonicalWorkforceEmploymentResult(
  value: unknown,
): value is UpsertCanonicalWorkforceEmploymentResult {
  return Value.Check(UpsertCanonicalWorkforceEmploymentResultSchema, value);
}

export function isDisableCanonicalWorkforceAccountCommand(
  value: unknown,
): value is DisableCanonicalWorkforceAccountCommand {
  return Value.Check(DisableCanonicalWorkforceAccountCommandSchema, value);
}

export function isWorkforceMutationPending(value: unknown): value is WorkforceMutationPending {
  return Value.Check(WorkforceMutationPendingSchema, value);
}

export function isWorkforceOffboardingPreview(
  value: unknown,
): value is WorkforceOffboardingPreview {
  return Value.Check(WorkforceOffboardingPreviewSchema, value);
}

export function isDisableCanonicalWorkforceAccountResult(
  value: unknown,
): value is DisableCanonicalWorkforceAccountResult {
  return Value.Check(DisableCanonicalWorkforceAccountResultSchema, value);
}
